import { useMemo } from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EllipsisOutlined } from '@ant-design/icons';
import {
    TPO_METRIC_GRANULARITY_ITEM,
    TPO_METRIC_KPI_VIEW_ITEM,
    TPO_METRIC_MORE_BTN,
    TPO_METRIC_TYPE_ITEM,
    tpoTableCellTestId,
    tpoTableColTestId,
} from '../../../../config/testIds';
import { useCustomerConfig, useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { resolveFieldLabel } from '../../../../utils/resolveFieldLabel';
import { PromoCell, PromoScalarCell, renderNumberCell } from '../../cellRenderers';
import { resolveMetricRowKey } from '../../utils/metricRowKey';
import { promoCellNumber, promoCellText } from '../../utils/promoCellValue';
import { resolveKpiFormat } from '../../utils/resolveKpiFormat';
import type {
    Granularity,
    KpiViewType,
    MetricType,
    PeriodColumn,
    PlanMetricMetricRow,
    PlanMetricRow,
    PromoMetricCell,
    PromoMetricRow,
    SubMode,
} from '../../types';

const PERIOD_COL_WIDTH: Record<Granularity, number> = {
    W: 160,
    M: 130,
    Q: 180,
};

const METRIC_TABLE_SURFACE = 'metric_table';

/**
 * AntD's cell/header prop bags are typed to their own props, which do not declare
 * `data-*` keys, so the object literal needs an assertion at that boundary.
 */
const headerCellAttrs = (columnKey: string) =>
    ({ 'data-testid': tpoTableColTestId(METRIC_TABLE_SURFACE, columnKey) }) as React.ThHTMLAttributes<HTMLTableCellElement>;

const bodyCellAttrs = (rowKey: string | undefined, columnKey: string) =>
    ({
        'data-testid': rowKey ? tpoTableCellTestId(METRIC_TABLE_SURFACE, rowKey, columnKey) : undefined,
    }) as React.TdHTMLAttributes<HTMLTableCellElement>;

const cellTestId = (rowKey: string | undefined, columnKey: string): string | undefined =>
    rowKey ? tpoTableCellTestId(METRIC_TABLE_SURFACE, rowKey, columnKey) : undefined;

interface PlanMetricColumnsArgs {
    subMode: SubMode;
    dimension: string;
    /** Selected KPI keys. In `dimension` sub-mode only the first is charted/formatted. */
    kpis: string[];
    periods: PeriodColumn[];
    granularity: Granularity;
    getColumnSortOrder: (key: string) => 'ascend' | 'descend' | null;
    onClickGranularity: () => void;
}

export function usePlanMetricColumns(args: PlanMetricColumnsArgs): ColumnsType<PlanMetricRow> {
    const { subMode, dimension, kpis, periods, granularity, getColumnSortOrder, onClickGranularity } = args;
    const { metricPageConfig } = useTpoGenConfig();
    const { getKpiByKey, metricFormatByKey } = useCustomerConfig();
    const planDimLabels = useMemo(
        () => new Map(metricPageConfig.planMetricDimensions.map((d) => [d.value, d.label])),
        [metricPageConfig],
    );

    // `dimension` sub-mode: every row is the same KPI, so one format covers the
    // whole grid. `metric` sub-mode: each ROW is a different KPI, so the format
    // has to be resolved per row from `kpi_id`.
    const rowFormatFor = useMemo(() => {
        if (subMode === 'dimension') {
            const single = resolveKpiFormat(kpis[0] ?? '', getKpiByKey(kpis[0] ?? ''), metricFormatByKey);
            return () => single;
        }
        return (record: PlanMetricRow) => {
            const kpiKey = (record as PlanMetricMetricRow).kpi_id ?? '';
            return resolveKpiFormat(kpiKey, getKpiByKey(kpiKey), metricFormatByKey);
        };
    }, [subMode, kpis, getKpiByKey, metricFormatByKey]);

    return useMemo<ColumnsType<PlanMetricRow>>(() => {
        const columns: ColumnsType<PlanMetricRow> = [];
        const periodWidth = PERIOD_COL_WIDTH[granularity];

        if (subMode === 'dimension') {
            columns.push({
                title: planDimLabels.get(dimension) ?? 'Dimension',
                dataIndex: 'attributeDesc',
                key: 'label',
                fixed: 'left',
                width: 200,
                sorter: true,
                sortOrder: getColumnSortOrder('label'),
                onHeaderCell: () => headerCellAttrs('label'),
                onCell: (record) => bodyCellAttrs(resolveMetricRowKey(record), 'label'),
                render: (value: string) => <span className="font-medium">{value}</span>,
            });
        } else {
            columns.push({
                title: 'Metric',
                dataIndex: 'kpi_name',
                key: 'label',
                fixed: 'left',
                width: 220,
                sorter: true,
                sortOrder: getColumnSortOrder('label'),
                onHeaderCell: () => headerCellAttrs('label'),
                onCell: (record) => bodyCellAttrs(resolveMetricRowKey(record), 'label'),
                render: (value: string) => <span className="font-medium">{value}</span>,
            });
        }

        columns.push({
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 140,
            sorter: true,
            sortOrder: getColumnSortOrder('total'),
            align: 'right',
            fixed: 'left',
            onHeaderCell: () => headerCellAttrs('total'),
            onCell: (record) => bodyCellAttrs(resolveMetricRowKey(record), 'total'),
            render: (value: number, record) =>
                renderNumberCell(value, rowFormatFor(record), cellTestId(resolveMetricRowKey(record), 'total')),
        });

        for (const period of periods) {
            columns.push({
                title: period.label,
                dataIndex: period.key,
                key: period.key,
                width: periodWidth,
                sorter: true,
                sortOrder: getColumnSortOrder(period.key),
                align: 'right',
                ellipsis: true,
                onHeaderCell: () => headerCellAttrs(period.key),
                onCell: (record) => bodyCellAttrs(resolveMetricRowKey(record), period.key),
                render: (value: number, record) =>
                    renderNumberCell(value, rowFormatFor(record), cellTestId(resolveMetricRowKey(record), period.key)),
            });
        }

        columns.push({
            title: (
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [
                            {
                                key: 'change_calendar_view',
                                label: 'Change Calendar View',
                                onClick: onClickGranularity,
                                'data-testid': TPO_METRIC_GRANULARITY_ITEM,
                            } as NonNullable<MenuProps['items']>[number],
                        ],
                    }}
                >
                    <button
                        type="button"
                        aria-label="Change Calendar view"
                        className="flex items-center justify-center w-full h-full p-0 m-0 bg-transparent border-0 cursor-pointer"
                        data-testid={TPO_METRIC_MORE_BTN}
                    >
                        <EllipsisOutlined />
                    </button>
                </Dropdown>
            ),
            dataIndex: '__more',
            key: '__more',
            width: 40,
            fixed: 'right',
            align: 'center',
            onHeaderCell: () => headerCellAttrs('more'),
            render: () => null,
        });

        return columns;
    }, [subMode, dimension, periods, granularity, getColumnSortOrder, onClickGranularity, planDimLabels, rowFormatFor]);
}

interface PromoMetricColumnsArgs {
    dimensions: string[];
    kpis: string[];
    kpiViewType: KpiViewType;
    metricType: MetricType;
    getColumnSortOrder: (key: string) => 'ascend' | 'descend' | null;
    onClickKpiView: () => void;
    onClickMetric: () => void;
}

export function usePromoMetricColumns(args: PromoMetricColumnsArgs): ColumnsType<PromoMetricRow> {
    const { dimensions, kpis, kpiViewType, metricType, getColumnSortOrder, onClickKpiView, onClickMetric } = args;
    const { getPromoKpiByKey, getPromoKpiName, metricFormatByKey } = useCustomerConfig();
    const { metricPageConfig, fieldValueLabelMaps } = useTpoGenConfig();
    const promoDimLabels = useMemo(
        () => new Map(metricPageConfig.promoMetricDimensions.map((d) => [d.value, d.label])),
        [metricPageConfig],
    );
    const dimResolvers = useMemo(
        () => new Map(metricPageConfig.promoMetricDimensions.map((d) => [d.value, d.resolveLabelFrom])),
        [metricPageConfig],
    );

    return useMemo<ColumnsType<PromoMetricRow>>(() => {
        const columns: ColumnsType<PromoMetricRow> = [];
        // Metric type (abs/percent) only applies to the actuals vs pre-snapshot comparison view.
        const isComparisonView = kpiViewType === 'actuals_vs_pre_snapshot';

        for (const dim of dimensions) {
            columns.push({
                title: promoDimLabels.get(dim) ?? dim,
                dataIndex: dim,
                key: dim,
                fixed: 'left',
                width: 180,
                sorter: true,
                sortOrder: getColumnSortOrder(dim),
                onHeaderCell: () => headerCellAttrs(dim),
                onCell: (record) => bodyCellAttrs(resolveMetricRowKey(record), dim),
                render: (value: string) => (
                    <span className="font-medium">
                        {resolveFieldLabel(value, fieldValueLabelMaps, dimResolvers.get(dim))}
                    </span>
                ),
            });
        }

        for (const kpiKey of kpis) {
            const kpi = getPromoKpiByKey(kpiKey);
            // `date` / `string` KPIs are single scalars: no change/previous pair to
            // lay out, so they stay narrow even in the comparison view. Alignment
            // still follows the numeric columns so the KPI block reads as one grid.
            const isScalarField = (kpi?.fieldType ?? 'number') !== 'number';
            const sortable = kpi?.sortable !== false;
            // Consumed only by the numeric branch below; `date` / `string` KPIs render
            // through `PromoScalarCell`, which never applies a currency or a precision.
            const format = resolveKpiFormat(kpiKey, kpi, metricFormatByKey);
            columns.push({
                title: kpi?.name ?? getPromoKpiName(kpiKey),
                dataIndex: kpiKey,
                key: kpiKey,
                width: isComparisonView && !isScalarField ? 280 : 160,
                sorter: !sortable
                    ? false
                    : isScalarField
                      ? (a, b) => promoCellText(a[kpiKey]).localeCompare(promoCellText(b[kpiKey]))
                      : (a, b) => promoCellNumber(a[kpiKey]) - promoCellNumber(b[kpiKey]),
                sortOrder: getColumnSortOrder(kpiKey),
                align: 'right',
                onHeaderCell: () => headerCellAttrs(kpiKey),
                onCell: (record) => bodyCellAttrs(resolveMetricRowKey(record), kpiKey),
                render: (value: PromoMetricCell, record) =>
                    isScalarField ? (
                        <PromoScalarCell value={value} testId={cellTestId(resolveMetricRowKey(record), kpiKey)} />
                    ) : (
                        <PromoCell
                            value={value}
                            kpiViewType={kpiViewType}
                            metricType={metricType}
                            format={format}
                            testId={cellTestId(resolveMetricRowKey(record), kpiKey)}
                        />
                    ),
            });
        }

        columns.push({
            title: (
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [
                            {
                                key: 'change_kpi_view',
                                label: 'Change KPI view',
                                onClick: onClickKpiView,
                                'data-testid': TPO_METRIC_KPI_VIEW_ITEM,
                            } as NonNullable<MenuProps['items']>[number],
                            {
                                key: 'change_metric',
                                label: 'Change Metric',
                                onClick: onClickMetric,
                                disabled: !isComparisonView,
                                'data-testid': TPO_METRIC_TYPE_ITEM,
                            } as NonNullable<MenuProps['items']>[number],
                        ],
                    }}
                >
                    <button
                        type="button"
                        aria-label="Change KPI view or metric"
                        className="flex items-center justify-center w-full h-full p-0 m-0 bg-transparent border-0 cursor-pointer"
                        data-testid={TPO_METRIC_MORE_BTN}
                    >
                        <EllipsisOutlined />
                    </button>
                </Dropdown>
            ),
            dataIndex: '__more',
            key: '__more',
            width: 40,
            fixed: 'right',
            align: 'center',
            onHeaderCell: () => headerCellAttrs('more'),
            render: () => null,
        });

        return columns;
    }, [
        dimensions,
        kpis,
        kpiViewType,
        metricType,
        getColumnSortOrder,
        onClickKpiView,
        onClickMetric,
        getPromoKpiByKey,
        getPromoKpiName,
        metricFormatByKey,
        promoDimLabels,
        dimResolvers,
        fieldValueLabelMaps,
    ]);
}
